import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Reveal } from "@/components/Reveal";
import { API_URL } from "@/lib/assets";

export default function Contacto() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API_URL}/contact`, form);
      setSent(true);
    } catch (err) {
      toast.error("No pudimos enviar tu mensaje. Inténtalo nuevamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contacto-page">
      <section className="py-20 lg:py-28">
        <div className="max-w-2xl mx-auto px-4 sm:px-8">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-mauve font-sans font-semibold">Contacto</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink mt-4 leading-[1.1]">
              Escríbenos.
            </h1>
            <p className="mt-8 text-base sm:text-lg font-light text-stone2 leading-relaxed">
              Si tienes consultas generales sobre el sitio, privacidad o sobre una postulación ya enviada, déjanos tu
              mensaje y te responderemos por correo.
            </p>
          </Reveal>

          {sent ? (
            <Reveal>
              <div data-testid="contacto-success" className="mt-14 text-center border border-line bg-cream p-12">
                <CheckCircle2 size={48} className="text-mauve mx-auto" strokeWidth={1.2} />
                <h2 className="font-serif text-3xl text-ink mt-6">Mensaje enviado.</h2>
                <p className="mt-4 text-base font-light text-stone2">Te responderemos a la brevedad.</p>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.1}>
              <form onSubmit={handleSubmit} data-testid="contact-form" className="mt-14 space-y-8">
                <div className="space-y-2">
                  <Label htmlFor="c_name" className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-ink">
                    Nombre
                  </Label>
                  <Input
                    id="c_name"
                    data-testid="contact-input-nombre"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none border-line bg-white focus-visible:ring-mauve h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c_email" className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-ink">
                    Correo
                  </Label>
                  <Input
                    id="c_email"
                    type="email"
                    data-testid="contact-input-email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="rounded-none border-line bg-white focus-visible:ring-mauve h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c_message" className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-ink">
                    Mensaje
                  </Label>
                  <Textarea
                    id="c_message"
                    data-testid="contact-textarea-mensaje"
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="rounded-none border-line bg-white focus-visible:ring-mauve min-h-[140px]"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="bg-plum text-ivory hover:bg-mauve rounded-none px-12 py-6 text-xs uppercase tracking-[0.18em] font-sans transition-colors duration-300"
                >
                  {sending ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={15} className="animate-spin" /> Enviando…
                    </span>
                  ) : (
                    "Enviar mensaje"
                  )}
                </Button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
